<template>
    <div class="main-page designer-workflow-page">
        <InfoPopup v-model="showWarning" :title="infoTitle" @confirm="onPopupConfirm" @cancel="onCancelGenerate">
            <p>{{ infoMessage }}</p>
        </InfoPopup>

        <header class="workflow-header">
            <div>
                <p class="eyebrow">Voice Designer</p>
                <h1>Shape the voice performance</h1>
                <p>Describe the tone, pace, and use case, then preview the generated result before saving.</p>
            </div>
        </header>

        <section class="designer-stepper" aria-label="Voice design progress">
            <article class="step-card done">
                <span class="step-number">1</span>
                <span><strong>Choose source</strong><small>{{ baseVoiceInfo?.voice_name || 'Base selected' }}</small></span>
            </article>
            <article class="step-card current">
                <span class="step-number">2</span>
                <span><strong>Shape voice</strong><small>Prompt and generate</small></span>
            </article>
            <article class="step-card">
                <span class="step-number">3</span>
                <span><strong>Save</strong><small>Name and tag</small></span>
            </article>
        </section>

        <div class="prompt-layout">
            <section class="prompt-panel">
                <div class="base-summary" v-if="baseVoiceInfo">
                    <button class="icon-btn" type="button" @click="toggleBaseAudioPlay" aria-label="Play base voice">
                        <span v-if="!isPlayingBase">Play</span>
                        <span v-else>Pause</span>
                    </button>
                    <div class="voice-text">
                        <strong>{{ baseVoiceInfo.voice_name }}</strong>
                        <span>{{ baseVoiceInfo.tags?.[0] || 'Base' }} voice ({{ baseAudioDuration }}s)</span>
                    </div>
                    <ul class="voice-tags" v-if="baseVoiceInfo?.tags && baseVoiceInfo.tags.length">
                        <li v-for="tag in baseVoiceInfo.tags" :key="tag">{{ tag }}</li>
                    </ul>
                </div>

                <div class="prompt-section">
                    <div class="section-head">
                        <div>
                            <h2>Describe how the new voice should perform</h2>
                            <p>Prompt chips help users add tone, pace, and character without guessing what to type.</p>
                        </div>
                        <span class="badge">Base ready</span>
                    </div>
                    <textarea id="promptText" v-model="promptText"
                        placeholder="Warm, articulate, and slightly upbeat. Speak like a calm product educator introducing a new feature to busy creators."></textarea>
                    <div class="prompt-groups">
                        <div class="prompt-group">
                            <h3>Tone</h3>
                            <button v-for="tone in toneOptions" :key="tone" class="prompt-chip" type="button"
                                @click="addSuggestion(tone)">
                                {{ tone }}
                            </button>
                        </div>
                        <div class="prompt-group">
                            <h3>Pace</h3>
                            <button v-for="pace in paceOptions" :key="pace" class="prompt-chip" type="button"
                                @click="addSuggestion(pace)">
                                {{ pace }}
                            </button>
                        </div>
                        <div class="prompt-group">
                            <h3>Use case</h3>
                            <button v-for="useCase in useCaseOptions" :key="useCase" class="prompt-chip" type="button"
                                @click="addSuggestion(useCase)">
                                {{ useCase }}
                            </button>
                        </div>
                        <div class="prompt-group">
                            <h3>Emotion</h3>
                            <button v-for="s in suggestions" :key="s" class="prompt-chip" type="button"
                                @click="addSuggestion(s)">
                                {{ s }}
                            </button>
                        </div>
                    </div>
                    <div class="actions-area">
                        <button class="primary-btn" type="button" @click="generate" :disabled="isGenerating">
                            <span v-if="isGenerating" class="spinner"></span>
                            <span v-else>Generate</span>
                        </button>
                        <p class="credit-info">
                            Credits:
                            {{ (typeof balance === 'number' && !isNaN(balance)) ? balance.toLocaleString() : '' }}
                        </p>
                    </div>
                </div>

                <section class="result-card">
                    <div class="result-card-head">
                        <div>
                            <h2>Generated voice result</h2>
                            <p>{{ generatedAudioUrl ? 'Preview this version, regenerate with the same prompt, or save it to your voice library.' : 'Generate once to preview and save a custom voice.' }}</p>
                        </div>
                        <span class="badge" :class="{ muted: !generatedAudioUrl }">{{ generatedAudioUrl ? 'Ready' : 'Waiting' }}</span>
                    </div>
                    <div class="audio-strip">
                        <button class="icon-btn" type="button" :disabled="!generatedAudioUrl" @click="playGeneratedAudio"
                            aria-label="Play latest generated audio">
                            Play
                        </button>
                        <div class="mini-wave" aria-hidden="true">
                            <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                        </div>
                        <span class="duration-note">{{ generatedAudioUrl ? 'Ready' : 'No result yet' }}</span>
                    </div>
                    <div class="result-actions">
                        <button class="secondary-btn" type="button" @click="generate" :disabled="isGenerating">Regenerate</button>
                        <button class="secondary-btn" type="button" @click="downloadGeneratedAudio"
                            :disabled="!generatedAudioUrl">Download</button>
                        <button class="primary-btn" type="button" @click="onSaveVoice"
                            :class="{ disabled: !generatedAudioUrl || isGeneratedAudioSaved }">
                            {{ isGeneratedAudioSaved ? 'Saved' : 'Save Voice' }}
                        </button>
                    </div>
                </section>
            </section>

            <aside class="history-panel">
                <h2>Generation history</h2>
                <p>Restore useful versions without relying on a tiny prompt preview.</p>
                <div class="history-list">
                    <article v-for="entry in sortedHistory" :key="entry.timestamp" class="history-row">
                        <div class="history-title">
                            <strong>{{ entry.payload.instruct_text.slice(0, 42) || 'Generated voice' }}</strong>
                            <span>{{ entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '' }}</span>
                        </div>
                        <p>{{ entry.payload.instruct_text }}</p>
                        <div class="mini-wave" aria-hidden="true">
                            <i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i><i></i>
                        </div>
                        <div class="history-actions">
                            <button class="icon-btn" type="button" @click="playHistory(entry)" aria-label="Play history item">Play</button>
                            <button class="secondary-btn" type="button" @click="downloadHistory(entry)">Download</button>
                            <button class="secondary-btn" type="button" @click="playHistory(entry)">Restore</button>
                        </div>
                    </article>
                    <article v-if="!sortedHistory.length" class="history-row empty">
                        <strong>No generated voices yet</strong>
                        <p>Your generated versions will appear here with playback and restore actions.</p>
                    </article>
                </div>
            </aside>
        </div>

        <audio class="hidden-audio" ref="footerAudioRef" :src="generatedAudioUrl"></audio>
        <footer class="page-footer">
            <div class="footer-summary">
                <div class="voice-avatar">{{ baseVoiceInfo?.voice_name?.charAt(0) || 'V' }}</div>
                <div>
                    <strong>{{ generatedAudioUrl ? 'Generated voice ready' : 'Prompt the new voice' }}</strong>
                    <span>{{ generatedAudioUrl ? 'Preview the result, save it, or generate another version.' : 'Generate before saving this voice.' }}</span>
                </div>
            </div>
            <div class="footer-actions">
                <button class="secondary-btn" type="button" @click="goToVD">Cancel</button>
                <button class="secondary-btn" type="button" @click="goBack">Back</button>
                <button class="primary-btn" type="button" @click="onSaveVoice"
                    :class="{ disabled: !generatedAudioUrl || isGeneratedAudioSaved }">
                    {{ isGeneratedAudioSaved ? 'Saved' : 'Save Voice' }}
                </button>
            </div>
        </footer>
        <SaveVoicePopup v-model="showSavePopup" :audio-url="generatedAudioUrl" :instruct-text="promptText"
            @cancel="onCancelSave" @saved="onVoiceSaved" />
    </div>
</template>

<script>
import { useRouter } from 'vue-router';
import { inject, ref, computed, onMounted } from 'vue'
import InfoPopup from '@/components/common/InfoPopup.vue';
import { getCreditBalance } from '@/api/credits';
import { postNewTTSRequest } from '@/api/services'
import { requireAuth } from '@/api/auth';
import SaveVoicePopup from '@/components/common/SaveVoicePopup.vue';
import IconMic from '@/assets/icons/iconMic.svg';
import Union1 from '@/assets/figs/designer/Union1.png';
import Union2 from '@/assets/figs/designer/Union2.png';
import Union3 from '@/assets/figs/designer/Union3.png';
import Union4 from '@/assets/figs/designer/Union4.png';
import Union5 from '@/assets/figs/designer/Union5.png';
import Union6 from '@/assets/figs/designer/Union6.png';
import { checkInput } from '@/utils/InputChecker';
export default {
    name: 'TextPrompting',
    components: { InfoPopup, SaveVoicePopup },
    setup() {
        const router = useRouter();
        const sharedData = inject('sharedData')
        if (!sharedData.value.baseAudioUrl) {
            console.log('push back')
            router.push({ name: 'choose-base-voice' });
            return {};
        }

        const isPlayingBase = ref(false);
        const baseAudioDuration = ref(0);

        const balance = ref(0);
        const footerAudioRef = ref(null);

        const baseAudioUrl = sharedData.value.baseAudioUrl
        const baseVoiceInfo = sharedData.value.baseVoiceInfo
        const promptText = ref('');
        const toneOptions = ['Warm', 'Articulate', 'Bright', 'Soft', 'Confident'];
        const paceOptions = ['Medium pace', 'Slow and precise', 'Fast but clear', 'Clear pauses'];
        const useCaseOptions = ['Product education', 'Podcast intro', 'Course narration', 'Support reply'];
        const suggestions = ['Happy', 'Sad', 'Angry', 'Excited', 'Slow', 'Fast pace'];
        const lastTTSPayload = ref(null);
        const generationHistory = ref([]);

        const sortedHistory = computed(() =>
            [...generationHistory.value].sort((a, b) => b.timestamp - a.timestamp)
        );
        const generatedAudioUrl = ref('');
        const savedAudioUrl = ref('');
        const isGeneratedAudioSaved = computed(() => !!generatedAudioUrl.value && savedAudioUrl.value === generatedAudioUrl.value);
        const isGenerating = ref(false);
        const lastPayload = ref({ baseAudioUrl: '', promptText: '' });

        const showWarning = ref(false);
        const infoTitle = ref('')
        const infoMessage = ref('')
        const showInfoPopupCancelButton = ref(true);
        const showSavePopup = ref(false);
        const infoPopupNextAction = ref('')

        onMounted(() => {
            calculateBaseDuration();
            if (footerAudioRef.value) {
                footerAudioRef.value.addEventListener('ended', () => {
                    isPlayingBase.value = false;
                });
            }
            loadBalance();
        });
        function addSuggestion(text) {
            promptText.value += text + ' ';
        }

        function arrayBufferToBase64(buffer) {
            let binary = '';
            const bytes = new Uint8Array(buffer);
            for (let i = 0; i < bytes.byteLength; i++) {
                binary += String.fromCharCode(bytes[i]);
            }
            return btoa(binary);
        }

        async function normalizePromptAudioBlob(blob) {
            const arrayBuffer = await blob.arrayBuffer();
            const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
            const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
            const maxDur = 29;
            const targetSampleRate = 24000;
            const duration = Math.min(audioBuffer.duration, maxDur);
            const startOffset = audioBuffer.duration > maxDur ? audioBuffer.duration - maxDur : 0;
            const frameCount = Math.ceil(duration * targetSampleRate);
            const offlineCtx = new OfflineAudioContext(1, frameCount, targetSampleRate);
            const src = offlineCtx.createBufferSource();

            src.buffer = audioBuffer;
            src.connect(offlineCtx.destination);
            src.start(0, startOffset, duration);

            const rendered = await offlineCtx.startRendering();
            const length = rendered.length * 2 + 44;
            const view = new DataView(new ArrayBuffer(length));

            function writeString(offset, str) {
                for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
            }

            writeString(0, 'RIFF');
            view.setUint32(4, 36 + rendered.length * 2, true);
            writeString(8, 'WAVE');
            writeString(12, 'fmt ');
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);
            view.setUint16(22, 1, true);
            view.setUint32(24, rendered.sampleRate, true);
            view.setUint32(28, rendered.sampleRate * 2, true);
            view.setUint16(32, 2, true);
            view.setUint16(34, 16, true);
            writeString(36, 'data');
            view.setUint32(40, rendered.length * 2, true);

            let offset = 44;
            const channel = rendered.getChannelData(0);
            for (let i = 0; i < rendered.length; i++) {
                let sample = Math.max(-1, Math.min(1, channel[i]));
                view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
                offset += 2;
            }

            return new Blob([view], { type: 'audio/wav' });
        }
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        async function calculateBaseDuration() {
            if (!baseAudioUrl) {
                goToVD();
                return;
            }
            try {
                const response = await fetch(baseAudioUrl);
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
                baseAudioDuration.value = Math.ceil(audioBuffer.duration);
            } catch (err) {
                console.error('Failed to calculate base audio duration', err);
            }
        }
        async function loadBalance() {
            if (!await requireAuth()) { return }
            try {
                balance.value = await getCreditBalance();
            } catch (err) {
                console.warn('Failed to load credit balance:', err);
                balance.value = null;
            }
        }

        function toggleBaseAudioPlay() {
            const audio = footerAudioRef.value;
            if (!audio) return;

            if (isPlayingBase.value) {
                audio.pause();
                isPlayingBase.value = false;
            } else {
                if (audio.src !== baseAudioUrl) {
                    audio.src = baseAudioUrl;
                    audio.load();
                    audio.currentTime = 0;
                }
                audio.play().then(() => {
                    isPlayingBase.value = true;
                }).catch(error => {
                    infoPopupNextAction.value = ''
                    infoTitle.value = 'Error!'
                    infoMessage.value = `Audio play error: ${error}`
                    showWarning.value = true;
                    showInfoPopupCancelButton.value = false;
                });
            }
        }
        async function generate() {
            const audio = footerAudioRef.value;
            if (!audio) return;
            audio.pause();

            const instructLengthLimit = 500;
            const checkInstruct = checkInput(promptText.value, instructLengthLimit);
            if (!checkInstruct.isLongEnough) {
                infoPopupNextAction.value = ''
                infoTitle.value = 'Warning!'
                infoMessage.value = `Instruct text is too long (${promptText.value.length} / ${instructLengthLimit} characters). Please shorten it.`
                showWarning.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            if (!checkInstruct.hasOnlyAllowed) {
                infoPopupNextAction.value = ''
                infoTitle.value = 'Warning!'
                infoMessage.value = "Instruct text contains invalid characters: " + checkInstruct.disallowedChars.join(' ') + ". Only English letters, numbers, and common punctuation are allowed."
                showWarning.value = true;
                showInfoPopupCancelButton.value = false;
                return;
            }
            const newBase = baseAudioUrl;
            const newPrompt = promptText.value;
            const blob = await normalizePromptAudioBlob(sharedData.value.baseAudioBlob);
            sharedData.value.baseAudioBlob = blob;
            const arrayBuffer = await blob.arrayBuffer();
            const base64Audio = arrayBufferToBase64(arrayBuffer);
            lastTTSPayload.value = {
                model: "IndexTTS2",
                text: "Hello and welcome. Imagine the possibilities, turning your words into crystal clear audio, whether you're narrating an audiobook, creating a podcast, or building an interactive voice assistant.",
                instruct_text: newPrompt,
                prompt_audio: base64Audio,
            };
            if (
                lastPayload.value.baseAudioUrl === newBase &&
                lastPayload.value.promptText === newPrompt
            ) {
                infoTitle.value = 'Warning!'
                infoMessage.value = 'You are generating the same voice a second time.'
                showWarning.value = true;
                infoPopupNextAction.value = 'generate'
                return;
            } else {
                lastPayload.value.baseAudioUrl = newBase;
                lastPayload.value.promptText = newPrompt;
                onConfirmGenerate();
            }
        }
        function onPopupConfirm() {
            if (infoPopupNextAction.value === 'generate') {
                onConfirmGenerate();
                return
            }
            else {
                infoPopupNextAction.value = '';
            }
        }
        function playHistory(entry) {
            promptText.value = entry.payload.instruct_text;

            generatedAudioUrl.value = entry.audioUrl;

            const audio = footerAudioRef.value;
            if (!audio) return;
            audio.pause();
            isPlayingBase.value = false;
            if (audio.src !== generatedAudioUrl.value) {
                audio.src = generatedAudioUrl.value;
            }
            audio.currentTime = 0;
            audio.load();
            audio.addEventListener('loadedmetadata', () => {
                audio.play()
                    .then(() => {
                    })
                    .catch(console.error);
            }, { once: true });
            isGenerating.value = false;
        }
        function downloadHistory(entry) {
            if (!entry.audioUrl) return;
            const link = document.createElement('a');
            link.href = entry.audioUrl;
            link.download = `voice-${entry.timestamp ? new Date(entry.timestamp).toISOString() : 'audio'}.wav`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
        function playGeneratedAudio() {
            if (!generatedAudioUrl.value) return;
            const audio = footerAudioRef.value;
            if (!audio) return;
            audio.pause();
            isPlayingBase.value = false;
            if (audio.src !== generatedAudioUrl.value) {
                audio.src = generatedAudioUrl.value;
            }
            audio.currentTime = 0;
            audio.load();
            audio.addEventListener('loadedmetadata', () => {
                audio.play().catch(console.error);
            }, { once: true });
        }
        function downloadGeneratedAudio() {
            if (!generatedAudioUrl.value) return;
            downloadHistory({
                audioUrl: generatedAudioUrl.value,
                timestamp: new Date()
            });
        }
        async function onConfirmGenerate() {
            isGenerating.value = true;
            try {
                const response = await postNewTTSRequest(lastTTSPayload.value);
                if (!response) {
                    throw new Error('TTS request was not sent.');
                }
                const contentType = response.headers.get('Content-Type') || '';
                if (contentType.includes('audio')) {
                    const _audioIdHeader = response.headers.get('x-audio-id');

                    const wavBlob = await response.blob();
                    const audioUrl = URL.createObjectURL(wavBlob);
                    generatedAudioUrl.value = audioUrl;
                    savedAudioUrl.value = '';
                }
                else {
                    const text = await response.text();
                    const message = text.replace(/^"|"$/g, '');
                    lastTTSPayload.value = null;
                    infoPopupNextAction.value = ''
                    infoTitle.value = 'Error!'
                    infoMessage.value = `TTS failed: ${message}`
                    showWarning.value = true;
                    showInfoPopupCancelButton.value = false;
                    return;
                }
            } catch (err) {
                lastTTSPayload.value = null;
                infoPopupNextAction.value = ''
                infoTitle.value = 'Error!'
                infoMessage.value = `Generate Speech error: ${err}`
                showWarning.value = true;
                showInfoPopupCancelButton.value = false;
                return
            } finally {
                isGenerating.value = false;
            }
            const audio = footerAudioRef.value;
            if (!audio) {
                loadBalance();
                return;
            }
            audio.pause();
            isPlayingBase.value = false;
            if (audio.src !== generatedAudioUrl.value) {
                audio.src = generatedAudioUrl.value;
            }
            audio.currentTime = 0;
            audio.load();
            audio.addEventListener('loadedmetadata', () => {
                audio.play()
                    .then(() => {
                        generationHistory.value.push({
                            payload: lastTTSPayload.value,
                            timestamp: new Date(),
                            audioUrl: generatedAudioUrl.value
                        });
                    })
                    .catch(console.error);
            }, { once: true });
            loadBalance();
        }
        function onCancelGenerate() {
            showWarning.value = false;
        }
        function onCancelSave() {
            showSavePopup.value = false;
        }
        function goToVD() {
            router.push({ name: 'voice-designer' });
        }
        function goBack() {
            router.push({ name: 'choose-base-voice' });
        }
        function goNext() {
            sharedData.value.instruct_text = promptText.value;
            sharedData.value.generatedAudioUrl = generatedAudioUrl.value;
            router.push({ name: 'save-voice' });
        }
        function onSaveVoice() {
            if (!generatedAudioUrl.value || isGeneratedAudioSaved.value) return;
            sharedData.value.instruct_text = promptText.value;
            sharedData.value.generatedAudioUrl = generatedAudioUrl.value;
            showSavePopup.value = true;
        }
        function onVoiceSaved() {
            savedAudioUrl.value = generatedAudioUrl.value;
        }
        return {
            balance,
            goNext,
            goBack,
            showSavePopup,
            onSaveVoice,
            baseAudioUrl,
            baseVoiceInfo,
            generatedAudioUrl,
            isGeneratedAudioSaved,
            promptText,
            generate,
            showWarning,
            infoTitle,
            infoMessage,
            showInfoPopupCancelButton,
            infoPopupNextAction,
            onConfirmGenerate,
            onPopupConfirm,
            onCancelGenerate,
            onCancelSave,
            onVoiceSaved,
            footerAudioRef,
            toneOptions,
            paceOptions,
            useCaseOptions,
            suggestions,
            addSuggestion,
            isPlayingBase,
            toggleBaseAudioPlay,
            baseAudioDuration,
            isGenerating,
            generationHistory,
            sortedHistory,
            playHistory,
            downloadHistory,
            playGeneratedAudio,
            downloadGeneratedAudio,
            goToVD,
            Union1,
            Union2,
            Union3,
            Union4,
            Union5,
            Union6,
            IconMic,
        };
    }
}
</script>

<style scoped>
.main-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: var(--background-white);
    overflow: hidden;
}

.page-header {
    top: 0;
    background-color: #ffffff;
    z-index: 10;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    padding: 16px 24px;
    border-bottom: 1px solid #E5E7EB;
    flex-direction: column;
    flex: 0 0 auto;
}


.single-step-block {
    width: 100%;
    min-height: 116px;
}

.single-step-container {
    width: 100%;
    min-height: 116px;
    border-radius: 10px;
    opacity: 1;
    border: 1px solid var(--purple);
    padding: 0 16px;
    box-sizing: border-box;
    overflow: hidden;
}

.step-img {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 1px solid black;
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
}

.text-img {
    width: 52px;
    height: 52px;
    border-radius: 50%;
    border: 1px solid var(--purple);
    margin: auto;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
}

.mic-svg {
    width: 32px;
    height: 32px;
    display: block;
    transition: filter 0.3s, color 0.3s;
}

.text-svg {
    width: 32px;
    height: 32px;
    color: black;
    display: block;
    filter: var(--purple-filter)
}

.step-indicator-container {
    margin-top: 1rem;
    width: 100%;
    min-height: 120px;
}

.step-record-textinfo {
    box-sizing: border-box;
    padding: 0;
    color: #000000CC;
    letter-spacing: 0px;
    text-align: left;
    min-width: 0;
}

.step-record-textinfo-header {
    font-weight: 700;
    font-size: clamp(24px, 2.2vw, 32px);
    line-height: 1.15;
    white-space: nowrap;
}

.step-record-textinfo-desc {
    font-weight: 500;
    font-size: 12px;
    line-height: 1.4;
    white-space: normal;
}

.step-point-right {
    width: 100%;
    box-sizing: border-box;
}

.point-container {
    width: 100%;
    height: 92px;
    background-repeat: no-repeat;
    background-position: center, center;
    background-size: contain;
}

.middle-line-container {
    width: 100%;
    min-height: 116px;
    display: flex;
    align-items: center;
}

.middle-line {
    width: 100%;
    height: 0px;
    border: 1px solid #D9D9D9;
}

.step-indicator-container :deep(.step-indicator-row) {
    display: grid;
    grid-template-columns: minmax(260px, 1fr) minmax(48px, 0.5fr) minmax(260px, 1fr);
    align-items: center;
    gap: 0;
}

.step-indicator-container :deep(.step-indicator-row > .el-col) {
    flex: none !important;
    max-width: none !important;
    width: auto !important;
    margin-left: 0 !important;
}

.step-indicator-container :deep(.step-card-layout) {
    height: 100%;
    display: grid;
    grid-template-columns: 64px minmax(0, 1fr) minmax(72px, 112px);
    align-items: center;
    gap: 12px;
}

.step-indicator-container :deep(.step-card-layout > .el-col) {
    flex: none !important;
    max-width: none !important;
    width: auto !important;
}

.step-indicator-container :deep(.step-arrows) {
    display: grid;
    grid-template-columns: repeat(3, minmax(18px, 1fr));
    align-items: center;
    gap: 4px;
}

.step-indicator-container :deep(.step-arrows > .el-col) {
    flex: none !important;
    max-width: none !important;
    width: auto !important;
}

.card-row {
    flex: 1 1 auto;
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(300px, 30%);
    gap: 1rem;
    width: 100%;
    padding: 1rem;
    min-height: 0;
    overflow: auto;
    box-sizing: border-box;
}

.card-col-left {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.base-section,
.prompt-section {
    min-width: 0;
}

.card-col-right {
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.audio-section h3 {
    margin-bottom: 8px;
    font-size: 18px;
    color: #333;
}

.voice-info {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin: 12px 0 0;
    background-color: #FAFBFD;
    padding: 8px 12px;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    min-width: 0;
}

.voice-main {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: flex-start;
    width: 100%;
    min-width: 0;
    flex-wrap: wrap;
}

.voice-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    margin: 0;
    padding: 0;
    margin-left: auto;
    justify-content: flex-end;
}

.play-btn {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    padding: 0;
    flex: 0 0 auto;
}

.voice-text {
    display: flex;
    flex-direction: column;
    min-width: 160px;
    flex: 1 1 220px;
}

.voice-subtitle {
    color: #6B7280;
    font-size: 14px;
    margin: 0;
}

.voice-name {
    font-size: 16px;
    font-weight: 600;
    margin: 0 16px 0 0;
    overflow-wrap: anywhere;
}


.voice-tags li {
    border: 1px solid #D1D5DB;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 14px;
    color: #374151;
}

.generate-history {
    flex: 1;
    border-radius: 8px;
    border: 1px solid var(--border-color);
    overflow: auto;
    padding: 1rem;
    min-height: 260px;
}

.history-list {
    border-top: 1px solid var(--border-color);
    list-style: none;
    padding: 0;
    margin: 0;
}

.history-list li {
    display: flex;
    flex-direction: row;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem 1rem;
}

.history-list li:hover {
    background: var(--purple-ultra-light)
}

.history-play-btn {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    margin-right: 8px;
}

.history-download-btn {
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
    margin-right: 8px;
}


.history-text {
    margin-right: 8px;
    font-weight: 500;
    overflow-wrap: anywhere;
}

.history-time {
    color: #6B7280;
    font-size: 12px;
}

.prompt-section {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.prompt-section h3 {
    margin-bottom: 8px;
    font-size: 18px;
    color: #333;
}

.prompt-section textarea {
    flex: 1 1 auto;
    min-height: 130px;
    max-height: 280px;
    width: 100%;
    resize: none;
    border: 1px solid #ccc;
    border-radius: 4px;
    font-size: 18px;
    font-family: inherit;
    margin-bottom: 8px;
    box-sizing: border-box;
}

.suggestions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}

.suggestion {
    background: #E5E7EB;
    color: #333;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    transition: background 0.2s;
    line-height: 1.35;
    max-width: 100%;
    overflow-wrap: anywhere;
}

.suggestion:hover {
    background: #D1D5DB;
}

.actions-area {
    margin-top: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 0;
    margin-left: 0;
    margin-right: 0;
}

.credit-info {
    color: #666;
    font-size: 14px;
    margin: 0;
    text-align: left;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
}

.btn-generate {
    background-color: var(--purple);
    color: white;
    border: none;
    padding: 12px 24px;
    border-radius: 4px;
    font-size: 16px;
    cursor: pointer;
    transition: background-color 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 120px;
    min-width: 120px;
    height: 40px;
    box-sizing: border-box;
}

.btn-generate:hover {
    background-color: var(--purple-light);
}

.btn-save {
    background: none;
    border: none;
    padding: 0;
    font: 1em var(--app-font);
    cursor: pointer;
    border-radius: 0;
    box-shadow: none;
    color: inherit;
}

.btn-save.disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: var(--text-primary);
    pointer-events: none;
}

.btn-save:not(.disabled):hover {
    color: var(--purple);
}

.spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    vertical-align: middle;
    /* Remove margin-right for proper centering */
    margin: 0;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.page-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    position: sticky;
    bottom: 0;
    background-color: #ffffff;
    z-index: 10;
    flex-direction: row;
    padding: 16px 24px;
    border-top: 1px solid #E5E7EB;
    margin-top: auto;
    min-height: 80px;
    flex: 0 0 auto;
}

.footer-actions {
    display: flex;
    flex-direction: row;
    gap: 2rem;
    flex: 0 0 auto;
}

audio.footer-audio {
    right: 50%;
    width: min(52vw, 500px);
    min-width: 220px;
    flex: 1 1 320px;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
    border-radius: 4px;
    margin-right: 0;
}

.btn-back {
    background: none;
    border: none;
    font-size: 16px;
    font-weight: 500;
    color: #6D64FF;
    cursor: pointer;
    padding: 8px 12px;
}

.btn-back:hover {
    background-color: rgba(109, 100, 255, 0.1);
    border-radius: 8px;
}

.svg-play {
    width: 20px;
    height: 20px;
    display: block;
    transition: filter 0.3s, color 0.3s;
    filter: black;
}

.svg-play:hover {
    filter: var(--purple-filter);
}

.svg-download {
    width: 32px;
    height: 32px;
    display: block;
    transition: filter 0.3s, color 0.3s;
    filter: black;
}

.svg-download:hover {
    filter: var(--purple-filter);
}

@media (max-height: 820px) {
    .page-header {
        padding: 12px 20px;
    }

    .step-indicator-container {
        margin-top: 0.75rem;
        min-height: 100px;
    }

    .single-step-block,
    .single-step-container,
    .middle-line-container {
        min-height: 96px;
    }

    .point-container {
        height: 76px;
    }

    .prompt-section textarea {
        min-height: 100px;
        max-height: 180px;
    }

    .generate-history {
        min-height: 220px;
    }

    .page-footer {
        min-height: 68px;
        padding: 12px 20px;
    }
}

@media (max-width: 1180px) {
    .card-row {
        grid-template-columns: 1fr;
    }

    .card-col-right {
        min-height: 260px;
    }

    .generate-history {
        min-height: 240px;
    }
}

@media (max-width: 980px) {
    .step-indicator-container {
        min-height: auto;
    }

    .step-indicator-container :deep(.step-indicator-row) {
        grid-template-columns: 1fr;
        gap: 0.75rem;
    }

    .middle-line-container {
        display: none;
    }

    .step-indicator-container :deep(.step-card-layout) {
        grid-template-columns: 56px minmax(0, 1fr) minmax(60px, 88px);
    }

    .page-footer {
        flex-wrap: wrap;
    }

    audio.footer-audio {
        order: 3;
        width: 100%;
        flex-basis: 100%;
        min-width: 0;
    }
}

@media (max-width: 620px) {
    .page-header {
        padding: 12px 16px;
    }

    .card-row {
        padding: 0.75rem;
    }

    .single-step-container {
        padding: 0 12px;
    }

    .step-indicator-container :deep(.step-card-layout) {
        grid-template-columns: 48px minmax(0, 1fr);
    }

    .step-point-right {
        display: none;
    }

    .voice-tags {
        margin-left: 0;
        justify-content: flex-start;
    }

    .btn-generate {
        width: 100%;
    }

    .credit-info {
        width: 100%;
        justify-content: flex-start;
    }

    .footer-actions {
        gap: 1rem;
    }
}

.designer-workflow-page {
    min-height: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    color: #111827;
    background:
        linear-gradient(120deg, rgba(124, 58, 237, 0.08), transparent 28%),
        linear-gradient(240deg, rgba(37, 99, 235, 0.07), transparent 30%),
        #f7f8fc;
    overflow: auto;
}

.workflow-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    padding: 20px 28px 14px;
    background: transparent;
    border: 0;
    flex: 0 0 auto;
}

.eyebrow {
    margin: 0 0 6px;
    color: #7c3aed;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
}

.workflow-header h1 {
    margin: 0;
    font-size: 26px;
    line-height: 1.15;
}

.workflow-header p,
.section-head p,
.history-panel p,
.voice-text span,
.footer-summary span,
.step-card small,
.duration-note,
.credit-info {
    color: #5f6b7d;
}

.workflow-header p {
    margin: 6px 0 0;
}

.designer-stepper {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    padding: 0 28px 18px;
}

.step-card {
    display: grid;
    grid-template-columns: 30px minmax(0, 1fr);
    gap: 10px;
    align-items: center;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.82);
}

.step-card.current {
    border-color: #c4b5fd;
    background: linear-gradient(135deg, #ffffff, #f8f5ff);
    box-shadow: inset 0 0 0 1px rgba(196, 181, 253, 0.45);
}

.step-card.done .step-number {
    color: #059669;
    background: #dcfce7;
}

.step-number {
    width: 30px;
    height: 30px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    color: #7c3aed;
    background: #f3f0ff;
    font-weight: 700;
}

.step-card strong,
.step-card small,
.voice-text strong,
.voice-text span,
.footer-summary strong,
.footer-summary span {
    display: block;
}

.prompt-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 360px;
    gap: 20px;
    align-items: start;
    padding: 0 28px 96px;
}

.prompt-panel,
.history-panel,
.result-card {
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.88);
    box-shadow: 0 10px 34px rgba(17, 24, 39, 0.06);
}

.prompt-panel {
    display: grid;
    gap: 14px;
    min-width: 0;
    padding: 18px;
}

.prompt-section,
.result-card,
.result-card-head,
.history-panel,
.history-row {
    text-align: left;
}

.base-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #f8fafc;
}

.voice-text {
    min-width: 0;
}

.voice-text strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.voice-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    list-style: none;
    margin: 0 0 0 auto;
    padding: 0;
}

.voice-tags li {
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    padding: 4px 8px;
    color: #475569;
    background: #f8fafc;
    font-size: 13px;
}

.prompt-section {
    display: grid;
    gap: 14px;
}

.section-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin: 0;
}

.section-head > div,
.result-card-head > div {
    min-width: 0;
    text-align: left;
}

.section-head h2,
.history-panel h2,
.result-card h2 {
    margin: 0;
    font-size: 19px;
    text-align: left;
}

.section-head p,
.history-panel p,
.result-card p {
    margin: 6px 0 0;
    line-height: 1.5;
    text-align: left;
}

.prompt-section textarea {
    min-height: 142px;
    max-height: none;
    margin: 0;
    padding: 14px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: linear-gradient(180deg, #ffffff, #fbfcff);
    color: #111827;
    line-height: 1.5;
    resize: vertical;
}

.prompt-groups {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.prompt-group h3 {
    margin: 0 0 8px;
    color: #5f6b7d;
    font-size: 13px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
}

.prompt-chip {
    margin: 0 5px 7px 0;
    padding: 7px 10px;
    border: 1px solid #dfe5ef;
    border-radius: 999px;
    color: #475569;
    background: #fff;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, color 180ms ease;
}

.prompt-chip:hover {
    border-color: #c4b5fd;
    color: #7c3aed;
    background: #f3f0ff;
}

.actions-area,
.result-actions,
.history-actions,
.footer-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin: 0;
}

.result-card {
    display: grid;
    gap: 14px;
    padding: 16px;
    background:
        linear-gradient(135deg, rgba(255, 255, 255, 0.96), rgba(247, 253, 250, 0.92)),
        radial-gradient(circle at 88% 0%, rgba(16, 185, 129, 0.14), transparent 32%);
}

.result-card-head {
    display: flex;
    justify-content: space-between;
    gap: 12px;
}

.badge {
    height: fit-content;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 4px 10px;
    border: 1px solid #bbf7d0;
    border-radius: 999px;
    color: #059669;
    background: #ecfdf5;
    font-size: 12px;
    line-height: 1.2;
    text-align: center;
}

.badge.muted {
    color: #64748b;
    background: #f8fafc;
    border-color: #dfe5ef;
}

.audio-strip {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: #f8fafc;
}

.mini-wave {
    height: 34px;
    display: flex;
    align-items: center;
    gap: 3px;
}

.mini-wave i {
    display: block;
    width: 4px;
    border-radius: 999px;
    background: linear-gradient(180deg, rgba(124, 58, 237, 0.82), rgba(37, 99, 235, 0.4));
}

.mini-wave i:nth-child(1) { height: 9px; }
.mini-wave i:nth-child(2) { height: 18px; }
.mini-wave i:nth-child(3) { height: 13px; }
.mini-wave i:nth-child(4) { height: 25px; }
.mini-wave i:nth-child(5) { height: 17px; }
.mini-wave i:nth-child(6) { height: 29px; }
.mini-wave i:nth-child(7) { height: 12px; }
.mini-wave i:nth-child(8) { height: 22px; }
.mini-wave i:nth-child(9) { height: 15px; }
.mini-wave i:nth-child(10) { height: 26px; }

.history-panel {
    position: sticky;
    top: 20px;
    display: grid;
    gap: 14px;
    padding: 18px;
}

.history-list {
    display: grid;
    gap: 10px;
    margin: 0;
    padding: 0;
    border: 0;
}

.history-row {
    display: grid;
    gap: 8px;
    padding: 12px;
    border: 1px solid #dfe5ef;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.86);
}

.history-title {
    display: flex;
    justify-content: space-between;
    gap: 10px;
}

.history-title strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.history-title span,
.history-row p,
.duration-note {
    color: #5f6b7d;
    font-size: 13px;
}

.history-row p {
    margin: 0;
    line-height: 1.45;
}

.icon-btn,
.primary-btn,
.secondary-btn {
    min-height: 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border-radius: 7px;
    cursor: pointer;
    transition: background 180ms ease, border-color 180ms ease, transform 180ms ease, color 180ms ease;
}

.icon-btn {
    min-width: 56px;
    height: 38px;
    padding: 0 10px;
    border: 1px solid #dfe5ef;
    color: #111827;
    background: #fff;
}

.primary-btn {
    padding: 9px 14px;
    border: 0;
    color: #fff;
    background: linear-gradient(135deg, #7c3aed, #6d5dfc);
    box-shadow: 0 12px 28px rgba(109, 93, 252, 0.18);
}

.secondary-btn {
    padding: 9px 14px;
    border: 1px solid #dfe5ef;
    color: #111827;
    background: #fff;
}

.icon-btn:hover,
.secondary-btn:hover,
.primary-btn:not(.disabled):hover {
    transform: translateY(-1px);
}

.primary-btn.disabled,
.secondary-btn:disabled,
.icon-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    pointer-events: none;
}

.spinner {
    width: 16px;
    height: 16px;
    margin: 0;
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-top-color: #fff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

.hidden-audio {
    display: none;
}

.page-footer {
    position: sticky;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    min-height: 72px;
    padding: 12px 28px;
    margin-top: auto;
    border-top: 1px solid #dfe5ef;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(14px);
}

.footer-summary {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}

.voice-avatar {
    width: 42px;
    height: 42px;
    display: grid;
    place-items: center;
    flex: 0 0 auto;
    border-radius: 7px;
    color: #7c3aed;
    background: linear-gradient(135deg, #ede9fe, #dbeafe);
    font-weight: 760;
    text-transform: uppercase;
}

button:focus-visible,
textarea:focus-visible {
    outline: 3px solid rgba(124, 58, 237, 0.24);
    outline-offset: 2px;
}

@media (max-width: 1120px) {
    .prompt-layout {
        grid-template-columns: 1fr;
    }

    .history-panel {
        position: static;
    }
}

@media (max-width: 760px) {
    .workflow-header,
    .page-footer {
        align-items: stretch;
        flex-direction: column;
    }

    .designer-stepper,
    .prompt-layout {
        padding-left: 16px;
        padding-right: 16px;
    }

    .designer-stepper,
    .prompt-groups {
        grid-template-columns: 1fr;
    }

    .workflow-header,
    .page-footer {
        padding-left: 16px;
        padding-right: 16px;
    }

    .base-summary,
    .result-card-head,
    .history-title {
        align-items: flex-start;
        flex-direction: column;
    }

    .voice-tags {
        margin-left: 0;
    }
}
</style>
